import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Backdrop,
  Fade,
  Box,
  Typography,
  Button,
  Grid,
  makeStyles,
  CircularProgress,
  IconButton,
  LinearProgress
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { useSnackbar } from "contexts/SnackbarContext";
import { getDocOcrableFilesList, ocrDocFilesForWorkspace } from "./FileApi";
import OcrPaymentStep from "./OcrPaymentStep";
import useOcrPayment from "./useOcrPayment";

const OCR_POLL_INTERVAL_MS = 2500;

const useStyles = makeStyles((theme) => ({
  ocrBtn: {
    background: "white",
    border: "1px solid #E0E0E0",
    boxShadow: "none",
    width: "10.5rem",
    textTransform: "none",
    [theme.breakpoints.down("sm")]: {
      width: "8.375rem",
    },
    [theme.breakpoints.down("xs")]: {
      width: "7.125rem",
    },
  },
  ocrBtnText: {
    fontSize: "0.9375rem",
    fontWeight: "500",
    textTransform: "none",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
    },
  },
  paper: {
    width: "450px",
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(4, 4, 3),
    outline: "none",
    borderRadius: "0.4rem",
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    boxShadow: theme.shadows[5],
    [theme.breakpoints.down("xs")]: {
      width: "90%",
      padding: theme.spacing(3, 2, 2),
    },
  },
  title: {
    fontSize: "1.25rem",
    fontWeight: "700",
    marginBottom: "1rem",
  },
  bodyText: {
    fontSize: "0.9375rem",
    fontWeight: "400",
    lineHeight: "1.6",
  },
  fileListScroll: {
    maxHeight: "150px",
    overflowY: "auto",
    border: "1px solid #E0E0E0",
    borderRadius: "4px",
    padding: "0.5rem 1rem",
    marginTop: "1rem",
    background: "#F9F9F9",
  },
  fileName: {
    fontSize: "0.8125rem",
    padding: "0.25rem 0",
    wordBreak: "break-all",
    borderBottom: "1px solid #F0F0F0",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  cancel: {
    fontSize: "1rem",
    fontWeight: "500",
    marginRight: "2rem",
    textTransform: "none",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
    },
  },
  confirmBtn: {
    textTransform: "none",
    padding: "0.5rem 1.5rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
    },
  },
  closeBtn: {
    position: "absolute",
    right: "0.5rem",
    top: "0.5rem",
  },
  spinner: {
    marginRight: "8px",
    color: theme.palette.primary.main,
  },
  loaderBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem 0",
  },
  loaderText: {
    marginTop: "1rem",
    fontSize: "0.9375rem",
  },
  progressWrapper: {
    marginTop: "1.5rem",
    marginBottom: "1.5rem",
  },
  progressText: {
    fontSize: "0.875rem",
    marginTop: "0.5rem",
    display: "flex",
    justifyContent: "space-between",
    fontWeight: "500",
  },
  paymentSummary: {
    border: "1px solid #E0E0E0",
    borderRadius: "4px",
    padding: "0.75rem 1rem",
    marginTop: "1rem",
    background: "#F9F9F9",
  },
  paymentRow: {
    fontSize: "0.875rem",
    padding: "0.25rem 0",
    display: "flex",
    justifyContent: "space-between",
  },
  paymentTotal: {
    fontWeight: "700",
    fontSize: "0.9375rem",
    borderTop: "1px solid #E0E0E0",
    marginTop: "0.5rem",
    paddingTop: "0.5rem",
  }
}));

export default function OcrWorkspace({ workspaceId, firstLevelFolderId, reload, paginationStatus, isEmpty, ocrData, setOcrData, disputeId, profile }) {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();
  const { payForOcr, paying } = useOcrPayment({ disputeId, profile });

  const [isOpen, setIsOpen] = useState(false);
  const [preCheckLoading, setPreCheckLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const hasPaidRef = useRef(false);

  const ocrableFiles = ocrData?.ocrable_files || [];
  const ocredCount = ocrData?.number_of_ocred_files || 0;
  const ocrableCount = ocrData?.number_of_ocrable_files || 0;
  const totalCount = ocredCount + ocrableCount;
  const progressPercent = totalCount > 0 ? Math.min(100, Math.round((ocredCount / totalCount) * 100)) : 0;

  const ocrInProgress = polling;
  const isOcrBtnDisabled =
    !workspaceId ||
    paginationStatus === 1 ||
    isEmpty;

  useEffect(() => {
    if (ocrData?.status === "OCR in progress") {
      setPolling(true);
    }
  }, [ocrData?.status]);

  useEffect(() => {
    if (!polling || !workspaceId) return undefined;

    let cancelled = false;
    let inFlight = false;

    const pollProgress = () => {
      if (inFlight) return; // never stack requests if one is slower than the interval
      inFlight = true;

      getDocOcrableFilesList(workspaceId, firstLevelFolderId)
        .then((res) => {
          if (cancelled) return;
          const data = res.data;
          setOcrData(data);

          const isFinished = data?.status !== "OCR in progress" || (data?.ocrable_files && data.ocrable_files.length === 0);

          if (isFinished) {
            setPolling(false);
            showSnackbar("OCR processing completed successfully!", "success");
            if (reload) reload();
            setIsOpen(false);
          }
        })
        .catch((err) => {
          console.error("Error polling OCR progress:", err);
        })
        .finally(() => {
          inFlight = false;
        });
    };

    pollProgress();
    const intervalId = setInterval(pollProgress, OCR_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [polling, workspaceId, firstLevelFolderId]);

  const handleOpen = () => {
    if (!workspaceId) {
      showSnackbar("Workspace ID not found.", "error");
      return;
    }
    setIsOpen(true);
    setPreCheckLoading(true);
    setPaymentInfo(null);
    hasPaidRef.current = false;
    getDocOcrableFilesList(workspaceId, firstLevelFolderId)
      .then((res) => {
        setOcrData(res.data);
        setPreCheckLoading(false);
        if (res.data?.status === "OCR in progress") {
          setPolling(true);
        }
      })
      .catch((err) => {
        console.error("Error pre-checking OCR files:", err);
        showSnackbar("Failed to scan workspace files for OCR.", "error");
        setPreCheckLoading(false);
        setIsOpen(false);
      });
  };

  const handleClose = () => {
    if (!triggerLoading && !paying) {
      setIsOpen(false);
    }
  };

  // The backend answers payment_required with a 4xx, so it can arrive on either branch.
  const showPaymentStep = (data) => {
    if (hasPaidRef.current) {
      showSnackbar("Payment received but OCR could not be started. Please try again.", "error");
      return;
    }
    setPaymentInfo(data);
  };

  const triggerOcr = () => {
    setTriggerLoading(true);
    return ocrDocFilesForWorkspace(workspaceId, firstLevelFolderId, {
      profile_id: profile?.id,
      dispute_id: disputeId,
    })
      .then((res) => {
        setTriggerLoading(false);
        if (res.data?.payment_required) {
          showPaymentStep(res.data);
          return;
        }
        setPaymentInfo(null);
        setPolling(true);
        showSnackbar("OCR processing started. Processing documents...", "info");
      })
      .catch((err) => {
        setTriggerLoading(false);
        const data = err.response?.data;
        if (data?.payment_required) {
          showPaymentStep(data);
          return;
        }
        const errMsg = data?.error || data?.message || "Failed to trigger OCR processing.";
        showSnackbar(errMsg, "error");
        if (errMsg?.toLowerCase()?.includes("progress") || errMsg?.toLowerCase()?.includes("complete")) {
          setPolling(true);
        }
      });
  };

  const handleConfirm = () => {
    triggerOcr();
  };

  const handlePayAndOcr = async () => {
    const paid = await payForOcr(paymentInfo?.amount);
    if (!paid) return;
    hasPaidRef.current = true;
    setPaymentInfo(null);
    triggerOcr();
  };

  return (
    <>
      <Button
        color="primary"
        onClick={handleOpen}
        className={classes.ocrBtn}
        disabled={isOcrBtnDisabled}
      >
        <Typography className={classes.ocrBtnText}>
          {polling ? "Extracting..." : "OCR"}
        </Typography>
      </Button>

      <Modal
        open={isOpen}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
        disableEnforceFocus
      >
        <Fade in={isOpen}>
          <Box className={classes.paper}>
            <IconButton
              className={classes.closeBtn}
              onClick={handleClose}
              disabled={triggerLoading || paying}
            >
              <Close />
            </IconButton>

            <Typography className={classes.title}>
              {paymentInfo ? "Payment required to continue OCR" : "OCR Documents"}
            </Typography>

            {preCheckLoading ? (
              <Box className={classes.loaderBox}>
                <CircularProgress size={40} />
                <Typography className={classes.loaderText}>
                  Scanning workspace for OCR-eligible files...
                </Typography>
              </Box>
            ) : polling ? (
              <>
                <Typography className={classes.bodyText}>
                  OCR is currently in progress. You can safely close this modal, and OCR will continue in the background.
                </Typography>
                <div className={classes.progressWrapper}>
                  <LinearProgress variant="determinate" value={progressPercent} />
                  <Typography className={classes.progressText}>
                    <span>{progressPercent}% completed</span>
                    <span>{ocredCount} / {totalCount} files</span>
                  </Typography>
                </div>
                <Grid container justify="flex-end">
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.confirmBtn}
                      onClick={handleClose}
                    >
                      Close
                    </Button>
                  </Grid>
                </Grid>
              </>
            ) : paymentInfo ? (
              <OcrPaymentStep
                paymentInfo={paymentInfo}
                paying={paying}
                onPay={handlePayAndOcr}
                onCancel={handleClose}
                classes={classes}
              />
            ) : (
              <>
                {ocrableFiles.length > 0 ? (
                  <>
                    <Typography className={classes.bodyText}>
                      We found <strong>{ocrableFiles.length}</strong> document(s) requiring OCR processing. Do you want to trigger OCR processing now?
                    </Typography>

                    <Box className={classes.fileListScroll}>
                      {ocrableFiles.map((file) => (
                        <Typography key={file.id} className={classes.fileName}>
                          {file.name}
                        </Typography>
                      ))}
                    </Box>

                    <Box height="2rem" />

                    <Grid container justify="flex-end">
                      <Grid item>
                        <Button
                          variant="outlined"
                          color="primary"
                          className={classes.cancel}
                          onClick={handleClose}
                          disabled={triggerLoading}
                        >
                          Cancel
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          className={classes.confirmBtn}
                          onClick={handleConfirm}
                          disabled={triggerLoading}
                        >
                          {triggerLoading ? (
                            <>
                              <CircularProgress size={16} className={classes.spinner} />
                              Processing...
                            </>
                          ) : (
                            "Confirm OCR"
                          )}
                        </Button>
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Typography className={classes.bodyText}>
                      No eligible documents require OCR processing in this workspace. All files are already searchable or processed.
                    </Typography>
                    <Box height="2rem" />
                    <Grid container justify="flex-end">
                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          className={classes.confirmBtn}
                          onClick={handleClose}
                        >
                          Close
                        </Button>
                      </Grid>
                    </Grid>
                  </>
                )}
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
}


